import { ROUTE_NAME, STORE } from '~/constants/strings'
import type { ITilesData, TBackground } from '~/constants/types/charts'
import { api } from '~/services'
import { useProjectDataVisualizationsStore } from './projectDataVisualizations'
import { HNRV2 } from '~/helpers'
import { AxiosError } from 'axios'
import { notifyFailure } from '~/utils/notifications'
import {
  EnumerationsDataVizSortBy,
  EnumerationsSortDirection,
  EnumerationsSignificanceFilter,
  type QueriesGetSRADataVisualizationResponse,
  type DataVizTileChartDataDTO,
  type QueriesSRATopLevelTileData,
  type DataVizTileStrataTileDataDTO,
  type TransferObjectsSignificantResultsSummaryDTO,
  type TransferObjectsViewByOptions,
  type QueriesGroups,
  type DataVizMeritVariableDTO,
  type QueriesGetSRAStatisticalValueSummaryRequest,
  EnumerationsAnalysisType,
} from 'client/sratool'
import type {
  FrequencyOfStatisticalImpactChart,
  StandardDeviationSeverityChart,
  SignificantResultsSummary,
  SummaryHashTable,
  SignificantResultsSummaryTable,
  StatisticalValuesSummaryTable,
  StatisticalValuesSummaryTableSortColumn,
  PickByPrefix,
  SelectionRateAnalysisPersonnelActivityType,
} from '~/constants/types/projectDataVisualizations'
import { useChartRefresh } from '~/composables/chart/useChartRefresh'
import {
  GET_DATA_FAILED,
  GET_SIGNIFICANT_RESULTS_SUMMARY_TABLE_FAILED,
  GET_STATISTICAL_VALUE_SUMMARY_TABLE_FAILED,
  REFRESH_SIGNIFICANT_RESULTS_SUMMARY_TABLE_FAILED,
  REFRESH_STATISTICAL_VALUE_SUMMARY_TABLE_FAILED,
} from '~/constants/projectDataVisualizations'
import type { ChartDataset } from 'chart.js'
import { useChartHasDataCheck } from '~/composables/chart/useChartHasDataCheck'
import { formatProtectedClass } from '~/helpers/executiveSummary'
import {
  useProjectDataVisualizationSelectionStore,
  type SelectionSnapshot,
} from './projectDataVisualizationsSelection'

export const useCompetitiveMovementsDataVisualizationsStore = defineStore(
  STORE.COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS,
  () => {
    const paType = ref<SelectionRateAnalysisPersonnelActivityType>()
    const data = ref<QueriesGetSRADataVisualizationResponse>()

    const wfaAPI = api.workforceAnalyticsDataVisualizations
    const ProjectDataVisualizationsStore = useProjectDataVisualizationsStore()
    const { isCurrent, getAnalysisData } =
      useProjectDataVisualizationSelectionStore()
    const { dataIsDefined, hasData } = useChartHasDataCheck()

    const getTilesAndBarCharts = async (
      selection: SelectionSnapshot,
      requestValidation?: typeof isCurrent
    ): Promise<QueriesGetSRADataVisualizationResponse | undefined> => {
      const selectedAnalysis = getAnalysisData(
        selection.analysisID,
        selection.proportionalAnalysisTypeID
      )

      if (!selection.analysisID || !selectedAnalysis?.analysisID) {
        return
      }

      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsSraTileDataPost({
            analysisID: selectedAnalysis.analysisID!,
            strataFieldGUID: selection.strataID,
            strataValue: selection?.strataValue,
            groupFieldGUID: selection?.groupFieldID,
          }),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        notifyFailure(GET_DATA_FAILED)
        data.value = undefined
        return
      }

      switch (selectedAnalysis.analysisTypeID) {
        case EnumerationsAnalysisType.ApplicantsVsHires:
          paType.value = {
            ShortName: 'Hire',
            LongName: 'Hires',
          }
          break
        case EnumerationsAnalysisType.ApplicantsVsCompetitivePromotions:
        case EnumerationsAnalysisType.NonCompetitivePromotions:
          paType.value = {
            ShortName: 'Promo',
            LongName: 'Promotions',
          }
          break
        case EnumerationsAnalysisType.TerminationsAll:
        case EnumerationsAnalysisType.InvoluntaryTerminations:
        case EnumerationsAnalysisType.VoluntaryTerminations:
          paType.value = {
            ShortName: 'Term',
            LongName: 'Terminations',
          }
          break
        default:
          paType.value = {
            ShortName: 'Selection',
            LongName: 'Selections',
          }
          break
      }

      data.value = response
      return response
    }

    /**
     * Transforms QueriesTopLevelTileData into an array of ITilesData for DataVizTiles.vue
     * @param tileData QueriesTopLevelTileData
     * @returns ITilesData[]
     */
    const strataBackgroundColors: TBackground[] = [
      'dark-gray',
      'orange',
      'purple',
      'blue-green',
      'red',
    ]
    function transformTopLevelTileDataToTiles(
      tileData: QueriesSRATopLevelTileData | undefined
    ): ITilesData[] {
      if (!tileData) return []
      // Map each top-level property to a tile
      return [
        {
          title: `Total ${paType.value?.LongName}`,
          numberDisplay: tileData.totalHires,
          icon: 'check_circle',
          background: 'blue',
          subText: `Overall selection rate ${tileData.percentageHireRate}%`,
        },

        // Strata tiles (example for up to 5)
        ...[1, 2, 3, 4, 5].reduce<ITilesData[]>((acc, index) => {
          const strata: DataVizTileStrataTileDataDTO =
            tileData[
              `strata${index}Tile` as keyof Pick<
                QueriesSRATopLevelTileData,
                | 'strata1Tile'
                | 'strata2Tile'
                | 'strata3Tile'
                | 'strata4Tile'
                | 'strata5Tile'
              >
            ]
          if (!strata) return acc

          acc.push({
            title: `Significant Results: ${strata.strataDisplayName}`,
            numberDisplay: `${strata.significantStrataPercentage}%`,
            icon: 'layers',
            background: strataBackgroundColors[index],
            subText: `Total Analyses: ${strata.totalStrataCount}`,
          })

          return acc
        }, []),
      ]
    }

    const tiles = computed(() => {
      return transformTopLevelTileDataToTiles(data.value?.topLevelTileData)
    })

    function transformFrequencyOfStatisticalImpactBarChartData<
      T extends FrequencyOfStatisticalImpactChart
    >({
      chartData,
      fieldName,
      type,
    }: {
      chartData: QueriesGetSRADataVisualizationResponse | undefined
      fieldName: 'frequencyOfStatisticalImpactByGroupField'
      type?: 'Hiring' | 'Promo' | undefined
    }): T[] {
      if (!chartData) return []

      // determine number of strata
      const strataInUse = [
        ...(chartData[
          `${fieldName}1` as keyof PickByPrefix<
            QueriesGetSRADataVisualizationResponse,
            typeof fieldName
          >
        ]?.chartDataByStrataByGroupFieldValue?.reduce<string[]>(
          (acc, index) => {
            if (!acc.includes(index.strataDisplayName)) {
              acc.push(index.strataDisplayName)
            }

            return acc
          },
          []
        ) || null),
      ]

      let frequencyLabelOffset = 0

      const labelSpacing = [] as string[]
      if (strataInUse.length === 1) {
        labelSpacing.push(' ')
        labelSpacing.push(' ')
        labelSpacing.push(' ')
        labelSpacing.push(' ')
        frequencyLabelOffset = 30
      } else if (strataInUse.length === 2) {
        labelSpacing.push(' ')
        labelSpacing.push(' ')
        frequencyLabelOffset = 15
      } else if (strataInUse.length === 3) {
        labelSpacing.push(' ')
        frequencyLabelOffset = 8
      }

      return [
        // Strata bars (example for up to 5)
        ...[1, 2, 3, 4, 5].reduce<T[]>((acc, index) => {
          const strata: DataVizTileChartDataDTO =
            chartData[
              `${fieldName}${index}` as keyof PickByPrefix<
                QueriesGetSRADataVisualizationResponse,
                typeof fieldName
              >
            ]

          if (
            !strata ||
            strata.groupDisplayName === '' ||
            (ProjectDataVisualizationsStore.selectedGroup &&
              strata.groupFieldGUID !==
                ProjectDataVisualizationsStore.selectedGroup.groupFieldGUID)
          ) {
            return acc
          }

          const barChartData = strata.chartDataByStrataByGroupFieldValue.reduce(
            (groupData, strataData) => {
              strataData.groupCounts.reduce((groupValues, currentGroup) => {
                const datum = currentGroup.percent

                if (!dataIsDefined(datum)) {
                  return groupValues
                }

                // ensure group exists first
                groupData[currentGroup.groupFieldValue] =
                  groupData[currentGroup.groupFieldValue] || []
                // ensure the stack exists in the group
                groupData[currentGroup.groupFieldValue][currentGroup.stack] =
                  groupData[currentGroup.groupFieldValue][currentGroup.stack] ||
                  []
                // push the value onto the nested array
                groupData[currentGroup.groupFieldValue][
                  currentGroup.stack
                ].push(datum)

                return groupValues
              }, {})

              return groupData
            },
            {} as Record<string, Record<string, number[]>>
          )

          const barChartDataSeparated =
            strata.chartDataByStrataByGroupFieldValue.reduce(
              (groupData, strataData) => {
                strataData.groupCounts.reduce((groupValues, currentGroup) => {
                  const datum = currentGroup.percent

                  if (!dataIsDefined(datum)) {
                    return groupValues
                  }
                  groupData[currentGroup.groupFieldValue] =
                    groupData[currentGroup.groupFieldValue] || []
                  groupData[currentGroup.groupFieldValue].push(
                    currentGroup.percent
                  )
                  return groupValues
                }, {})

                return groupData
              },
              {} as Record<string, number[]>
            )

          const axisLabelsWithStacks =
            strata.chartDataByStrataByGroupFieldValue.reduce(
              (axisLabels, strataData) => {
                strataData.groupCounts.reduce((stacks, currentGroup) => {
                  axisLabels[strataData.strataDisplayName] =
                    axisLabels[strataData.strataDisplayName] || []

                  // only add stacks if they've been customized
                  if (currentGroup.stack !== 'default') {
                    const stackName = `${currentGroup.stack}`

                    if (
                      axisLabels[strataData.strataDisplayName].indexOf(
                        stackName
                      ) === -1
                    ) {
                      axisLabels[strataData.strataDisplayName].push(stackName)
                    }
                  }
                  return stacks
                }, [])

                return axisLabels
              },
              {} as Record<string, string[]>
            )

          const datasets: ChartDataset<'bar'>[] = []
          Object.entries(barChartData).forEach(([label, stacks]) => {
            Object.entries(stacks).forEach(([stackName, dataPoints]) => {
              datasets.push({
                label: label,
                data: dataPoints,
                stack: stackName,
              })
            })
          })

          if (!hasData(datasets)) {
            return acc
          }

          const labels = Object.entries(axisLabelsWithStacks).map(
            ([label, stacks]) => {
              // when adding stacks, need to get some spacing between each one or they all cram together
              // additionally, lineheight spacing out everything, but we only want it between the stacks
              // so here, making the stack name on one line, the "by {strata}" on the second, and two spacers
              // this aligns relatively well with the bars
              if (stacks.length > 0) {
                return [
                  ...stacks.flatMap((stack) => [
                    stack,
                    `by ${label}`,
                    ...labelSpacing,
                  ]),
                ]
              }

              return label
            }
          )

          const protectedClass = strata.groupDisplayName
          const executiveSummaryId = `FrequencyOfStatisticalImpact${formatProtectedClass(
            protectedClass as string
          )}${type ? type : ''}`

          acc.push({
            id: index,
            protectedClass,
            chart: {
              data: {
                labels: labels,
                datasets: datasets,
              },
              separatedChartData: {
                labels: Object.entries(axisLabelsWithStacks).reduce<string[]>(
                  (acc, [label, stacks]) => {
                    acc.push(...stacks.map((stack) => `${stack} by ${label}`))
                    return acc
                  },
                  []
                ),
                datasets: Object.keys(barChartDataSeparated).map((group) => {
                  return {
                    label: group,
                    data: barChartDataSeparated[group],
                  }
                }),
              },
            },
            insight: strata.chartInsight,
            frequencyLabelOffset,
            executiveSummaryId,
          } as T)

          return acc
        }, []),
      ]
    }

    const buildFrequencyOfStatisticalImpactCharts = (
      data: QueriesGetSRADataVisualizationResponse | undefined,
      type?: 'Hiring' | 'Promo' | undefined
    ) => {
      return transformFrequencyOfStatisticalImpactBarChartData<FrequencyOfStatisticalImpactChart>(
        {
          chartData: data,
          fieldName: 'frequencyOfStatisticalImpactByGroupField',
          type,
        }
      )
    }

    const frequencyOfStatisticalImpactCharts = computed<
      FrequencyOfStatisticalImpactChart[]
    >(() => {
      return buildFrequencyOfStatisticalImpactCharts(data.value)
    })

    function transformStandardDeviationSeverityBarChartData<
      T extends StandardDeviationSeverityChart
    >({
      chartData,
      fieldName,
    }: {
      chartData: QueriesGetSRADataVisualizationResponse | undefined
      fieldName: 'standardDeviationSeverityByGroupField'
    }): T[] {
      if (!chartData) return []

      const { dataIsDefined, hasData } = useChartHasDataCheck()

      return [
        // Strata bars (example for up to 5)
        ...[1, 2, 3, 4, 5].reduce<T[]>((acc, index) => {
          const strata: DataVizTileChartDataDTO =
            chartData[
              `${fieldName}${index}` as keyof PickByPrefix<
                QueriesGetSRADataVisualizationResponse,
                typeof fieldName
              >
            ]

          if (
            !strata ||
            strata.groupDisplayName === '' ||
            (ProjectDataVisualizationsStore.selectedGroup &&
              strata.groupFieldGUID !==
                ProjectDataVisualizationsStore.selectedGroup.groupFieldGUID)
          ) {
            return acc
          }

          const barChartData = strata.chartDataByStrataByGroupFieldValue.reduce(
            (groupData, strataData) => {
              strataData.groupCounts.reduce((groupValues, currentGroup) => {
                const datum = currentGroup.percent

                if (!dataIsDefined(datum)) {
                  return groupValues
                }

                groupData[currentGroup.groupFieldValue] =
                  groupData[currentGroup.groupFieldValue] || []
                groupData[currentGroup.groupFieldValue].push(datum)

                return groupValues
              }, {})

              return groupData
            },
            {} as Record<string, number[]>
          )

          const datasets = Object.keys(barChartData).map((group) => {
            return {
              label: group,
              data: barChartData[group],
            }
          })

          if (!hasData(datasets)) {
            return acc
          }

          acc.push({
            id: index,
            protectedClass: strata.groupDisplayName,
            chart: {
              data: {
                labels: strata.chartDataByStrataByGroupFieldValue.map(
                  (c) => c.strataDisplayName
                ),
                datasets,
              },
            },
            insight: strata.chartInsight,
          } as T)

          return acc
        }, []),
      ]
    }

    const standardDeviationSeverityCharts = computed<
      StandardDeviationSeverityChart[]
    >(() => {
      return transformStandardDeviationSeverityBarChartData<StandardDeviationSeverityChart>(
        {
          chartData: data.value,
          fieldName: 'standardDeviationSeverityByGroupField',
        }
      )
    })

    const meritVariables = ref<DataVizMeritVariableDTO[]>([])

    const significantResultsSummaryTables = ref<
      SummaryHashTable<SignificantResultsSummaryTable>
    >({})

    const buildSignificantResultsHierarchy = (
      rows: TransferObjectsSignificantResultsSummaryDTO[]
    ) => {
      const hashTable = Object.create(null)
      rows.forEach(
        (row) =>
          (hashTable[row.hierarchy] = {
            hierarchyID: row.hierarchy,
            data: row,
            children: [],
            expanded: false,
          })
      )
      const dataTree: Array<SignificantResultsSummary> = []
      rows.forEach((row) => {
        if (row.parentNode && hashTable[row.parentNode]) {
          hashTable[row.parentNode].children.push(hashTable[row.hierarchy])
        } else {
          dataTree.push(hashTable[row.hierarchy])
        }
      })
      return dataTree
    }

    const loadSignificantResultsSummaryTables = async (
      selection: SelectionSnapshot,
      requestValidation?: typeof isCurrent
    ) => {
      const selectedAnalysis = getAnalysisData(
        selection.analysisID,
        selection.proportionalAnalysisTypeID
      )
      if (!selection.analysisID || !selectedAnalysis) {
        return
      }

      const numberOfResults = 10
      const sortByID: EnumerationsDataVizSortBy =
        EnumerationsDataVizSortBy.HighestStatValue

      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsSignificantResultsSummaryPost({
            analysisID: selectedAnalysis?.analysisID as string,
            numberOfResults,
            sortByID,
            strataFieldGUID: selection?.strataID,
            strataValuesFilter: selection.strataValue
              ? [selection.strataValue]
              : null,
            groupFieldGUID: selection?.groupFieldID,
          }),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        meritVariables.value = []
        significantResultsSummaryTables.value = {}

        notifyFailure(GET_SIGNIFICANT_RESULTS_SUMMARY_TABLE_FAILED)
        return
      }

      meritVariables.value = response.meritVariables

      significantResultsSummaryTables.value =
        ProjectDataVisualizationsStore.groupOptions.reduce<
          SummaryHashTable<SignificantResultsSummaryTable>
        >((acc, currentGroup) => {
          const isNotSelectedGroupFilter =
            ProjectDataVisualizationsStore.selectedGroup &&
            currentGroup.groupFieldGUID !==
              ProjectDataVisualizationsStore.selectedGroup.groupFieldGUID

          const responseDataForGroup =
            response.significantResultsSummaryDataResponse.filter(
              (row) => row.groupFieldGUID === currentGroup.groupFieldGUID
            )

          if (isNotSelectedGroupFilter || responseDataForGroup.length === 0) {
            return acc
          }
          acc[currentGroup.groupDisplayName] = {
            groupField: currentGroup,
            data: buildSignificantResultsHierarchy(
              responseDataForGroup[0].significantResultsSummaryData
            ),
          }

          return acc
        }, {})
    }

    const refreshSignificantResultsSummaryTable = async (
      selection: SelectionSnapshot,
      selectedSortByOption: EnumerationsDataVizSortBy,
      groupField: QueriesGroups,
      selectedViewByOption?: TransferObjectsViewByOptions,
      requestValidation?: typeof isCurrent
    ) => {
      const numberOfResults = 10

      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsRefreshSignificantResultsSummaryPost({
            analysisID: selection?.analysisID as string,
            numberOfResults,
            sortByID: selectedSortByOption,
            strataValuesFilter: selection.strataValue
              ? [selection.strataValue]
              : null,
            groupFieldGUID: groupField.groupFieldGUID,
            viewByStrataFieldGUID: selectedViewByOption?.strataGUID,
          }),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        notifyFailure(REFRESH_SIGNIFICANT_RESULTS_SUMMARY_TABLE_FAILED)
        return
      }

      significantResultsSummaryTables.value[groupField.groupDisplayName] = {
        groupField: groupField,
        data: buildSignificantResultsHierarchy(
          response.significantResultsSummaryData
        ),
      }
    }

    const viewByOptions = ref<TransferObjectsViewByOptions[]>([])

    const statisticalValuesSummaryTables = ref<
      SummaryHashTable<StatisticalValuesSummaryTable>
    >({})

    const loadStatisticalValuesSummaryTables = async (
      selection: SelectionSnapshot,
      requestValidation?: typeof isCurrent
    ) => {
      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsStatisticalValueSummaryPost({
            analysisID: selection?.analysisID as string,
            viewByStrataFieldGUID:
              selection?.strataID ??
              ProjectDataVisualizationsStore.strataOptions[0].strataFieldGUID,
            strataValuesFilter: selection.strataValue
              ? [selection.strataValue]
              : null,
            groupFieldGUID: selection?.groupFieldID,
            getAllRecords: false,
            significanceFilter: EnumerationsSignificanceFilter.Significant,
          }),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        viewByOptions.value = []
        statisticalValuesSummaryTables.value = {}

        notifyFailure(GET_STATISTICAL_VALUE_SUMMARY_TABLE_FAILED)
        return
      }

      viewByOptions.value = response.viewByOptions

      statisticalValuesSummaryTables.value =
        ProjectDataVisualizationsStore.groupOptions.reduce<
          SummaryHashTable<StatisticalValuesSummaryTable>
        >((acc, currentGroup) => {
          const isNotSelectedGroupFilter =
            ProjectDataVisualizationsStore.selectedGroup &&
            currentGroup.groupFieldGUID !==
              ProjectDataVisualizationsStore.selectedGroup.groupFieldGUID
          const responseDataForGroup =
            response.statisticalValuesSummaryDataResponse.filter(
              (table) => table.groupFieldGUID === currentGroup.groupFieldGUID
            )
          if (isNotSelectedGroupFilter || responseDataForGroup.length === 0) {
            return acc
          }

          acc[currentGroup.groupDisplayName] = {
            groupField: currentGroup,
            data: responseDataForGroup[0],
          }

          return acc
        }, {})
    }

    const refreshStatisticalValuesSummaryTable = async (
      selection: SelectionSnapshot,
      selectedViewByOption: TransferObjectsViewByOptions,
      selectedMinimumSignificanceOption: EnumerationsSignificanceFilter,
      groupField: QueriesGroups,
      currentPage: number,
      sortColumn: StatisticalValuesSummaryTableSortColumn = null,
      sortDirection: EnumerationsSortDirection = EnumerationsSortDirection.Ascending,
      requestValidation?: typeof isCurrent
    ) => {
      const payload: QueriesGetSRAStatisticalValueSummaryRequest = {
        analysisID: selection?.analysisID as string,
        viewByStrataFieldGUID: selectedViewByOption.strataGUID,
        strataValuesFilter: selection.strataValue
          ? [selection.strataValue]
          : null,
        groupFieldGUID: groupField.groupFieldGUID,
        significanceFilter: selectedMinimumSignificanceOption,
        page: currentPage,
        pageSize: 10,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
      }

      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsRefreshStatisticalValueSummaryAnalysisIDPost(
            selection?.analysisID as string,
            payload
          ),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        notifyFailure(REFRESH_STATISTICAL_VALUE_SUMMARY_TABLE_FAILED)
        return
      }

      statisticalValuesSummaryTables.value[groupField.groupDisplayName] = {
        groupField: groupField,
        data: response,
      }
    }

    const getCharts = async (selection: SelectionSnapshot) => {
      ProjectDataVisualizationsStore.loading.chart = true

      resetData()

      await Promise.all([
        // tiles
        // bar charts
        getTilesAndBarCharts(selection, isCurrent),

        // sign results summary table
        loadSignificantResultsSummaryTables(selection, isCurrent),

        // stat values summary table
        loadStatisticalValuesSummaryTables(selection, isCurrent),
      ])

      ProjectDataVisualizationsStore.loading.chart = false
    }

    useChartRefresh({
      fetchChartFunction: getCharts,
      routeName: ROUTE_NAME.COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS,
    })

    const resetData = () => {
      data.value = undefined
      statisticalValuesSummaryTables.value = {}
      significantResultsSummaryTables.value = {}
    }

    return {
      resetData,
      // ready for display
      tiles,

      buildFrequencyOfStatisticalImpactCharts,
      frequencyOfStatisticalImpactCharts,

      standardDeviationSeverityCharts,

      viewByOptions,
      statisticalValuesSummaryTables,

      meritVariables,
      significantResultsSummaryTables,

      getTilesAndBarCharts,
      getSelectionRateAnalysesCharts: getCharts,
      refreshSignificantResultsSummaryTable,
      refreshStatisticalValuesSummaryTable,

      // raw
      data,
    }
  }
)
